import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, Plus, QrCode, Loader2, Building2, Pencil, ClipboardCheck, Check, X } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";

function QRDisplay({ workplace, onClose }) {
  const [data, setData] = useState(null);
  const [secs, setSecs] = useState(30);
  useEffect(() => {
    let alive = true;
    const fetchQr = async () => {
      try { const r = await api.get(`/workplaces/${workplace.id}/qr`); if (alive) { setData(r.data); setSecs(30); } } catch {}
    };
    fetchQr();
    const rot = setInterval(fetchQr, 27000);
    const tick = setInterval(() => setSecs((s) => (s <= 1 ? 30 : s - 1)), 1000);
    return () => { alive = false; clearInterval(rot); clearInterval(tick); };
  }, [workplace.id]);
  const url = data ? `${window.location.origin}/scan?d=${encodeURIComponent(data.token)}` : "";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0D0C] border-emerald-500/30 max-w-md text-center" data-testid="qr-display">
        <DialogHeader><DialogTitle className="text-emerald-400">{workplace.name}</DialogTitle>
          <DialogDescription>Employees scan this code with their phone camera to clock in or out. It refreshes automatically.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          <div className="bg-white p-5 rounded-2xl">
            {url ? <QRCodeCanvas value={url} size={240} level="M" /> : <div className="w-[240px] h-[240px] grid place-items-center"><Loader2 className="animate-spin text-emerald-600" /></div>}
          </div>
          <p className="text-sm text-muted-foreground">Refreshing in <span className="text-emerald-400 font-semibold">{secs}s</span></p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ workplace, isSuper, onDone, onClose }) {
  const [f, setF] = useState({
    name: workplace.name || "", address: workplace.address || "",
    lat: workplace.lat ?? null, lng: workplace.lng ?? null,
    geofence_enabled: !!workplace.geofence_enabled, location_strict: !!workplace.location_strict,
    radius_m: workplace.radius_m ?? 150, open_time: workplace.open_time || "09:00",
    close_time: workplace.close_time || "17:00", active: workplace.active !== false,
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/change-requests", { target_type: "workplace", target_id: workplace.id, changes: f, reason });
      if (data.applied) toast.success("Workplace updated.");
      else toast.success("Change request submitted for Super Admin approval.");
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121816] border-emerald-500/20 max-h-[90vh] overflow-y-auto" data-testid="workplace-edit-dialog">
        <DialogHeader><DialogTitle>Edit {workplace.name}</DialogTitle>
          {!isSuper && <DialogDescription>Your changes will be sent to the Super Admin for approval.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-workplace-name" /></div>
          <div><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-workplace-address" /></div>
          <div><Label>Workplace location <span className="text-muted-foreground font-normal">(required for on-site location clock-in)</span></Label><div className="mt-1.5"><LocationPicker lat={f.lat} lng={f.lng} onChange={(la, lo) => setF({ ...f, lat: la, lng: lo })} /></div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Opens</Label><Input type="time" value={f.open_time} onChange={(e) => setF({ ...f, open_time: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-workplace-open" /></div>
            <div><Label>Closes</Label><Input type="time" value={f.close_time} onChange={(e) => setF({ ...f, close_time: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-workplace-close" /></div>
          </div>
          <div><Label>Verification radius (m)</Label><Input type="number" value={f.radius_m} onChange={(e) => setF({ ...f, radius_m: parseInt(e.target.value) || 0 })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-workplace-radius" /></div>
          <div className="flex items-center justify-between rounded-lg bg-white/5 p-3">
            <div><p className="text-sm font-medium">Location check</p><p className="text-xs text-muted-foreground">Verify device location against the radius on QR clock-in</p></div>
            <Switch checked={f.geofence_enabled} onCheckedChange={(v) => setF({ ...f, geofence_enabled: v })} data-testid="edit-workplace-geofence" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-white/5 p-3">
            <div><p className="text-sm font-medium">Strict location</p><p className="text-xs text-muted-foreground">Block clock-in when outside the radius (otherwise flag for review)</p></div>
            <Switch checked={f.location_strict} onCheckedChange={(v) => setF({ ...f, location_strict: v })} data-testid="edit-workplace-strict" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-white/5 p-3">
            <div><p className="text-sm font-medium">Active</p></div>
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="edit-workplace-active" />
          </div>
          {!isSuper && <div><Label>Reason for change</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this change needed?" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-workplace-reason" /></div>}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy || !f.name} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="edit-workplace-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isSuper ? "Save changes" : "Request change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Workplaces() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", geofence_enabled: false, radius_m: 150, lat: null, lng: null });

  const load = async () => {
    const [w, r] = await Promise.allSettled([api.get("/workplaces"), api.get("/change-requests")]);
    if (w.status === "fulfilled") setItems(w.value.data);
    if (r.status === "fulfilled") setRequests(r.value.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/workplaces", form); toast.success("Workplace created."); setOpen(false); setForm({ name: "", address: "", geofence_enabled: false, radius_m: 150, lat: null, lng: null }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decideRequest = async (r, decision) => {
    try { await api.post(`/change-requests/${r.id}/decision`, { decision }); toast.success(`Request ${decision}.`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pendingReqs = requests.filter((r) => r.status === "pending");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Workplaces</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-workplace-btn"><Plus className="w-4 h-4 mr-1" /> Add workplace</Button></DialogTrigger>
          <DialogContent className="bg-[#121816] border-emerald-500/20 max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New workplace</DialogTitle><DialogDescription>Create a location where employees clock in. Set its location so on-site staff can clock in with GPS.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="workplace-name-input" /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="workplace-address-input" /></div>
              <div><Label>Workplace location <span className="text-muted-foreground font-normal">(required for on-site location clock-in)</span></Label><div className="mt-1.5"><LocationPicker lat={form.lat} lng={form.lng} onChange={(la, lo) => setForm({ ...form, lat: la, lng: lo })} /></div></div>
              <div><Label>Clock-in radius (m)</Label><Input type="number" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: parseInt(e.target.value) || 0 })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="workplace-radius-input" /></div>
              <div className="flex items-center justify-between rounded-lg bg-white/5 p-3">
                <div><p className="text-sm font-medium">Location check</p><p className="text-xs text-muted-foreground">Flag clock-ins outside the radius for review</p></div>
                <Switch checked={form.geofence_enabled} onCheckedChange={(v) => setForm({ ...form, geofence_enabled: v })} data-testid="workplace-geofence-switch" />
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={busy || !form.name} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="workplace-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pendingReqs.length > 0 && (
        <div className="card-emerald p-5 border-purple-500/25" data-testid="change-requests-panel">
          <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="w-5 h-5 text-purple-300" /><h3 className="font-display font-semibold">Change requests</h3></div>
          <div className="space-y-2.5">
            {pendingReqs.map((r) => (
              <div key={r.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-white/5" data-testid="change-request-item">
                <div>
                  <p className="text-sm font-medium">{r.target_name} · {r.requester_name}</p>
                  <p className="text-xs text-muted-foreground">{Object.entries(r.changes).map(([k, v]) => `${k}: ${v}`).join(", ")}{r.reason ? ` — "${r.reason}"` : ""}</p>
                </div>
                {isSuper ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decideRequest(r, "approved")} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217]" data-testid="cr-approve-btn"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => decideRequest(r, "rejected")} className="border-red-500/40 text-red-300" data-testid="cr-reject-btn"><X className="w-4 h-4 mr-1" /> Reject</Button>
                  </div>
                ) : <Badge className="bg-amber-500/20 text-amber-300">Pending approval</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.length === 0 ? <p className="text-muted-foreground">No workplaces yet. Add one to start QR attendance.</p> : items.map((w) => (
          <div key={w.id} className="card-emerald p-5 flex items-start justify-between" data-testid="workplace-card">
            <div>
              <div className="flex items-center gap-2 flex-wrap"><p className="font-display font-semibold">{w.name}</p>{!w.active && <Badge className="bg-red-500/20 text-red-300">Inactive</Badge>}{w.geofence_enabled && <Badge className="bg-emerald-500/20 text-emerald-300">Geofenced</Badge>}{w.location_strict && <Badge className="bg-amber-500/20 text-amber-300">Strict</Badge>}</div>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {w.address || "No address"}</p>
              <p className="text-xs text-muted-foreground mt-1">{w.open_time || "09:00"}–{w.close_time || "17:00"} · radius {w.radius_m ?? 150}m</p>
              <p className="text-xs mt-1" data-testid="workplace-location-status">{w.lat != null && w.lng != null ? <span className="text-emerald-300">● Location set — staff can clock in on-site</span> : <span className="text-amber-300">● No location set — edit to pin it so on-site clock-in works</span>}</p>
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" size="sm" onClick={() => setQr(w)} className="border-emerald-500/40" data-testid="display-qr-btn"><QrCode className="w-4 h-4 mr-1" /> QR</Button>
              <Button variant="outline" size="sm" onClick={() => setEdit(w)} className="border-emerald-500/40" data-testid="edit-workplace-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
            </div>
          </div>
        ))}
      </div>
      {qr && <QRDisplay workplace={qr} onClose={() => setQr(null)} />}
      {edit && <EditDialog workplace={edit} isSuper={isSuper} onDone={() => { setEdit(null); load(); }} onClose={() => setEdit(null)} />}
    </div>
  );
}
